// Store-and-forward diagnostics for device-side failures that Sentry cannot
// deliver. Motivating case: the Tesla in-car browser drops the header logo
// image and, on the same dying connection, the Sentry report about it —
// Sentry's buffer is memory-only, so the evidence vanishes with the page.
//
// This channel survives that: reports queue in localStorage immediately and
// flush to the client_diagnostics table (20260815100000) on the next load
// with working connectivity. Best-effort everywhere — diagnostics must never
// break the page they are diagnosing.
import { supabase } from "@/integrations/supabase/client";
import { getAppVersionInfo } from "@/hooks/use-app-version-tracker";

const QUEUE_KEY = "cha-client-diagnostics-queue";
const MAX_QUEUED = 5;

interface DiagnosticRow {
  kind: string;
  details: Record<string, unknown>;
  user_agent: string | null;
}

// client_diagnostics isn't in the generated Supabase types until Lovable
// regenerates them — call .from through a loose cast (established precedent).
const looseInsert = (rows: DiagnosticRow[]) =>
  (supabase as unknown as {
    from: (table: string) => {
      insert: (rows: DiagnosticRow[]) => PromiseLike<{ error: { message: string } | null }>;
    };
  })
    .from("client_diagnostics")
    .insert(rows);

const readQueue = (): DiagnosticRow[] => {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeQueue = (rows: DiagnosticRow[]): void => {
  try {
    if (rows.length === 0) localStorage.removeItem(QUEUE_KEY);
    else localStorage.setItem(QUEUE_KEY, JSON.stringify(rows.slice(0, MAX_QUEUED)));
  } catch {
    // Storage full or blocked — drop silently.
  }
};

async function trySend(rows: DiagnosticRow[]): Promise<boolean> {
  try {
    const { error } = await looseInsert(rows);
    return !error;
  } catch {
    return false;
  }
}

/**
 * Report a diagnostic event. Queued first (so it survives an imminent page
 * death or dead network), then an immediate delivery attempt; whatever fails
 * to send flushes on a later page load.
 */
export function reportClientDiagnostic(kind: string, details: Record<string, unknown>): void {
  const row: DiagnosticRow = {
    kind,
    details: {
      ...details,
      release: getAppVersionInfo().commitHash,
      queued_at: new Date().toISOString(),
    },
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 400) : null,
  };
  const pending = [...readQueue(), row].slice(-MAX_QUEUED);
  writeQueue(pending);
  void trySend(pending).then((sent) => {
    if (sent) writeQueue([]);
  });
}

/**
 * Deliver anything a previous session couldn't. Called once at startup,
 * delayed so it never competes with the page's own first requests.
 */
export function flushClientDiagnostics(): void {
  if (typeof window === "undefined") return;
  window.setTimeout(() => {
    const pending = readQueue();
    if (pending.length === 0) return;
    void trySend(pending).then((sent) => {
      if (sent) writeQueue([]);
    });
  }, 8000);
}
